import { Stack, Typography, Box, Container, Paper, Divider } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { lightTheme, brand } from '../components/themes/main_theme.js';
import Header from '../components/menu/Header.tsx';
import familyphoto2 from '../images/familyphoto2.png';

/**
 * The founders' story — the most personal content on the site.
 *
 * It used to sit inside a single boxed panel as one unbroken wall of text with
 * a small 250px photo. It is now laid out editorially: a large portrait, the
 * two introductions as distinct voices, and the ministry's history broken into
 * readable passages with a pull quote.
 */
const AboutUs = () => (
  <ThemeProvider theme={lightTheme}>
    <Header name={'ABOUT US'} />

    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 10 }}>
      <Typography variant="h2" color="primary" sx={{ mb: 1 }}>
        Ashley &amp; Alex Lopez
      </Typography>
      <Typography variant="overline" color="secondary" sx={{ display: 'block', mb: { xs: 3, md: 5 } }}>
        Founders · Salt and Light Ministries
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
        {/* Portrait — sticks alongside the story on desktop. */}
        <Box
          sx={{
            width: { xs: '100%', md: 380 },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: { md: 96 },
          }}
        >
          <Box
            component="img"
            src={familyphoto2}
            alt="Ashley and Alex Lopez with their family"
            sx={{
              width: '100%',
              aspectRatio: '3 / 4',
              objectFit: 'cover',
              borderRadius: 4,
              display: 'block',
              boxShadow: '0 12px 32px rgba(58,40,32,0.18)',
            }}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          {/* Two voices, given their own space rather than run together. */}
          <Stack spacing={2.5} sx={{ mb: 5 }}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, border: `1px solid ${brand.hairline}` }}>
              <Typography variant="h5" color="primary" sx={{ mb: 1 }}>I'm Ashley</Typography>
              <Typography color="text.secondary">
                I was born in the USA, but moved to Guatemala with my parents and younger sister
                in 2002. I have been teaching English as a second language (even though I never
                studied for it!) for about 20 years.
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, border: `1px solid ${brand.hairline}` }}>
              <Typography variant="h5" color="primary" sx={{ mb: 1 }}>I'm Alex</Typography>
              <Typography color="text.secondary">
                I was born in Guatemala and have lived here my whole life. I have been a teacher
                for about 20 years as well.
              </Typography>
            </Paper>
          </Stack>

          <Typography variant="h3" color="primary" sx={{ mb: 2 }}>
            How the ministry began
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            We got married in 2008 and worked as teachers together for many years. We felt called
            to share our light as the Bible tells us in Matthew chapter 13 through our English
            classes. That's when we decided to start SALEA (Salt And Light English Academy).
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 4 }}>
            We have been teaching English as a second language to our students since 2012, always
            being sure to use our own personal testimony, stories from the Bible, and prayer in
            our classes.
          </Typography>

          {/* Pull quote — the turning point of the story. */}
          <Box
            sx={{
              position: 'relative',
              borderLeft: `4px solid ${brand.gold}`,
              pl: { xs: 2.5, md: 4 },
              py: 1,
              my: 4,
            }}
          >
            <FormatQuoteIcon sx={{ color: brand.gold, opacity: 0.35, fontSize: 40, mb: -1 }} />
            <Typography
              variant="h4"
              color="primary"
              sx={{ fontStyle: 'italic', lineHeight: 1.4 }}
            >
              In 2021, amidst the COVID pandemic, we felt God wanted us to use our Academy
              building for something more.
            </Typography>
          </Box>

          <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            We wanted to receive work teams to come help the people of Guatemala. So we added
            rooms, bathrooms, and a full kitchen to our building and received our first team in
            June of 2022.
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            Now we are working with pastors from different villages nearby to help provide
            casitas, food bags, and — most importantly — the Gospel to the people who need it
            most in SALM (Salt And Light Ministries).
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography color="text.secondary">
            In order to keep the two ministries functioning together, we have been able to use
            our students as volunteer translators for the teams when they come.
          </Typography>
        </Box>
      </Stack>
    </Container>
  </ThemeProvider>
);

export default AboutUs;
